import React, { useState, useEffect } from 'react';
import Analytics from '../services/analytics';
import OtherCalcs from './OtherCalcs';

export interface QuizQuestion {
  id: number;
  labelText: string;
  question: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
}

export interface QuizScoreMessages {
  perfect: [string, string];
  good: [string, string];
  needsWork: [string, string];
}

export interface QuizData {
  title: string;
  description: string;
  questions: QuizQuestion[];
  scoreMessages: QuizScoreMessages;
  analyticsName: string;
}

interface QuizEngineProps {
  quizData: QuizData;
}

const QuizEngine: React.FC<QuizEngineProps> = ({ quizData }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanationDismissed, setExplanationDismissed] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);

  const currentQ = quizData.questions[currentQuestion];

  useEffect(() => {
    // Track when quiz is first interacted with
    if (!quizStarted && selectedAnswer) {
      setQuizStarted(true);
      Analytics.trackQuizEvent(quizData.analyticsName, 'quiz_start');
    }
  }, [selectedAnswer, quizStarted, quizData.analyticsName]);

  const handleAnswerSelect = (answer: string) => {
    // Prevent selection while modal is open
    if (showExplanation) {
      return;
    }
    setSelectedAnswer(answer);
  };

  const handleSubmit = () => {
    if (!showExplanation && !explanationDismissed) {
      setShowExplanation(true);
      if (selectedAnswer === currentQ.correctAnswer) {
        setCorrectAnswers(correctAnswers + 1);
      }
    } else {
      // Move to next question or complete quiz
      if (currentQuestion === quizData.questions.length - 1) {
        setQuizComplete(true);
        // Track quiz completion
        const finalScore = explanationDismissed
          ? correctAnswers + (selectedAnswer === currentQ.correctAnswer ? 1 : 0)
          : correctAnswers;
        Analytics.trackQuizEvent(
          quizData.analyticsName,
          'quiz_complete',
          Math.round((finalScore / quizData.questions.length) * 100)
        );
      } else {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer('');
        setShowExplanation(false);
        setExplanationDismissed(false);
      }
    }
  };

  const handleModalNext = () => {
    if (currentQuestion === quizData.questions.length - 1) {
      setQuizComplete(true);
      // Track quiz completion
      Analytics.trackQuizEvent(
        quizData.analyticsName,
        'quiz_complete',
        Math.round(
          ((correctAnswers + (selectedAnswer === currentQ.correctAnswer ? 1 : 0)) / quizData.questions.length) * 100
        )
      );
    } else {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer('');
      setShowExplanation(false);
      setExplanationDismissed(false);
    }
  };

  const handleModalClose = () => {
    setShowExplanation(false);
    setExplanationDismissed(true);
  };

  const handleModalOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      handleModalClose();
    }
  };

  const getFinalMessage = () => {
    const score = correctAnswers / quizData.questions.length;
    if (score === 1) {
      return quizData.scoreMessages.perfect;
    } else if (score > 0.5) {
      return quizData.scoreMessages.good;
    } else {
      return quizData.scoreMessages.needsWork;
    }
  };

  const handleTryAgain = () => {
    // Track quiz restart
    Analytics.trackQuizEvent(quizData.analyticsName, 'quiz_restart');
    setCurrentQuestion(0);
    setSelectedAnswer('');
    setShowExplanation(false);
    setExplanationDismissed(false);
    setCorrectAnswers(0);
    setQuizComplete(false);
    setQuizStarted(false);
  };

  const getButtonText = () => {
    if (!selectedAnswer) return 'Check Answer';
    if (!showExplanation && !explanationDismissed) return 'Check Answer';
    if (currentQuestion === quizData.questions.length - 1) return 'See Results →';
    return 'Next Question →';
  };

  if (quizComplete) {
    const [scoreHeader, scoreDescription] = getFinalMessage();

    return (
      <>
        <div className="quiz-results">
          <h3>{scoreHeader}</h3>
          <p>
            You got {correctAnswers} out of {quizData.questions.length} correct.
          </p>
          <p>{scoreDescription}</p>
          <button onClick={handleTryAgain} className="quiz-button">
            Try Again
          </button>
        </div>
        <OtherCalcs />
      </>
    );
  }

  return (
    <div className="quiz-engine">
      <div className="quiz-content">
        <div className="question-container">
          <h3>
            Question {currentQuestion + 1} of {quizData.questions.length}
          </h3>
          <div className="label-example">
            <pre>{currentQ.labelText}</pre>
          </div>
          <p className="question-text">{currentQ.question}</p>
        </div>

        <div className="options-container">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              className={`option-button ${selectedAnswer === option ? 'selected' : ''} ${
                showExplanation || explanationDismissed
                  ? option === currentQ.correctAnswer
                    ? 'correct'
                    : 'incorrect'
                  : ''
              }`}
              onClick={() => handleAnswerSelect(option)}
              disabled={showExplanation || explanationDismissed}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div className="quiz-button-container">
        <button onClick={handleSubmit} disabled={!selectedAnswer} className="quiz-button">
          {getButtonText()}
        </button>
      </div>

      {/* Modal for explanation */}
      {showExplanation && (
        <div className="quiz-modal-overlay" onClick={handleModalOverlayClick}>
          <div className="quiz-modal">
            <button className="quiz-modal-close" onClick={handleModalClose}>
              ×
            </button>
            <div
              className={`explanation-content ${selectedAnswer === currentQ.correctAnswer ? 'correct' : 'incorrect'}`}
            >
              <h4>{selectedAnswer === currentQ.correctAnswer ? '✓ Correct!' : '✗ Incorrect'}</h4>
              <p>{currentQ.explanation}</p>
            </div>
            <button onClick={handleModalNext} className="quiz-modal-button">
              {currentQuestion === quizData.questions.length - 1 ? 'See Results →' : 'Next Question →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizEngine;
